import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FaArrowLeft, FaSyncAlt } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import { pageMeta } from '../../../access/pageMeta'
import {
    Banner,
    Button,
    Column,
    DataTable,
    EmptyState,
    SearchInput,
    SegmentedControl,
    StatCard,
    StatusPill,
} from '../../../ui'
import {
    OrgActivity,
    RANGE_OPTIONS,
    RangeKey,
    TimelineUser,
    UserActivitySummary,
    Visit,
    rangeBounds,
} from './types'
import { formatRelative } from './format'
import { Timeline } from './Timeline'
import styles from './styles.module.scss'

const PAGE_SIZE = 100
const NO_ORG = '(none)'

const isRangeKey = (value: string | null): value is RangeKey => RANGE_OPTIONS.some((o) => o.value === value)

const errorMessage = (err: any, fallback: string) => err?.response?.data?.message || err?.message || fallback

/**
 * Organisations → users → one user's visits. URL state (`?org=`, `?user=`,
 * `?range=`) so the Users page can deep-link and the back button works.
 */
export const UserActivityView = () => {
    const [params, setParams] = useSearchParams()
    const org = params.get('org')
    const userParam = params.get('user')
    const range: RangeKey = isRangeKey(params.get('range')) ? (params.get('range') as RangeKey) : '7d'
    // Bumped by the Refresh button. Every fetch below depends on `bounds`, so
    // recomputing it (with a fresh "now") re-runs whichever level is showing.
    const [refreshTick, setRefreshTick] = useState(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const bounds = useMemo(() => rangeBounds(range), [range, refreshTick])

    const [find, setFind] = useState('')
    const [debouncedFind, setDebouncedFind] = useState('')
    useEffect(() => {
        const handle = window.setTimeout(() => setDebouncedFind(find.trim()), 300)
        return () => window.clearTimeout(handle)
    }, [find])

    const update = useCallback(
        (next: { org?: string | null; user?: string | null; range?: RangeKey }) => {
            const out = new URLSearchParams(params)
            const apply = (key: string, value: string | null | undefined) => {
                if (value === undefined) return
                if (value === null || value === '') out.delete(key)
                else out.set(key, value)
            }
            apply('org', next.org)
            apply('user', next.user)
            apply('range', next.range)
            setParams(out)
        },
        [params, setParams],
    )

    // ── Availability (migration applied?) ───────────────────────────────────
    const [unavailable, setUnavailable] = useState<string | null>(null)

    // ── Level 1: organisations ──────────────────────────────────────────────
    const [orgs, setOrgs] = useState<OrgActivity[]>([])
    const [orgsLoading, setOrgsLoading] = useState(false)
    const [orgsError, setOrgsError] = useState<string | null>(null)

    useEffect(() => {
        if (org || userParam || debouncedFind.length >= 2) return
        let active = true
        setOrgsLoading(true)
        setOrgsError(null)
        api.post('activity-organisations', bounds)
            .then(({ data }) => {
                if (!active) return
                if (data?.available === false) setUnavailable(data.message || 'Activity log unavailable')
                else if (data?.success) setOrgs(data.organisations || [])
                else setOrgsError(data?.message || 'Could not load organisations')
            })
            .catch((err) => active && setOrgsError(errorMessage(err, 'Could not load organisations')))
            .finally(() => active && setOrgsLoading(false))
        return () => {
            active = false
        }
    }, [org, userParam, debouncedFind, bounds])

    // ── Level 2: users (in an org, or matching the find box) ────────────────
    const [users, setUsers] = useState<UserActivitySummary[]>([])
    const [topPages, setTopPages] = useState<{ page: string; visits: number }[]>([])
    const [usersLoading, setUsersLoading] = useState(false)
    const [usersError, setUsersError] = useState<string | null>(null)
    const searching = debouncedFind.length >= 2

    useEffect(() => {
        if (userParam) return
        if (!org && !searching) return
        let active = true
        setUsersLoading(true)
        setUsersError(null)
        api.post('activity-users', {
            ...bounds,
            organisation: searching ? null : org,
            q: searching ? debouncedFind : '',
        })
            .then(({ data }) => {
                if (!active) return
                if (data?.available === false) setUnavailable(data.message || 'Activity log unavailable')
                else if (data?.success) {
                    setUsers(data.users || [])
                    setTopPages(data.topPages || [])
                } else setUsersError(data?.message || 'Could not load users')
            })
            .catch((err) => active && setUsersError(errorMessage(err, 'Could not load users')))
            .finally(() => active && setUsersLoading(false))
        return () => {
            active = false
        }
    }, [org, userParam, searching, debouncedFind, bounds])

    // ── Level 3: one user's timeline ────────────────────────────────────────
    const [timelineUser, setTimelineUser] = useState<TimelineUser | null>(null)
    const [visits, setVisits] = useState<Visit[]>([])
    const [visitsTotal, setVisitsTotal] = useState(0)
    const [visitsTruncated, setVisitsTruncated] = useState(false)
    const [timelineLoading, setTimelineLoading] = useState(false)
    const [timelineError, setTimelineError] = useState<string | null>(null)
    const [resolvedId, setResolvedId] = useState<{ email: string; cognitoId: string } | null>(null)

    // `?user=` carries the email (readable, shareable); the API wants the
    // Cognito id. Resolve once per email, over the full retention window so a
    // user with no recent activity still resolves.
    useEffect(() => {
        if (!userParam) return
        if (resolvedId && resolvedId.email.toLowerCase() === userParam.toLowerCase()) return
        let active = true
        setTimelineLoading(true)
        setTimelineError(null)
        setVisits([])
        setTimelineUser(null)
        api.post('activity-users', { ...rangeBounds('90d'), q: userParam })
            .then(({ data }) => {
                if (!active) return
                if (data?.available === false) {
                    setUnavailable(data.message || 'Activity log unavailable')
                    return
                }
                const list: UserActivitySummary[] = data?.users || []
                const match =
                    list.find((u) => u.email.toLowerCase() === userParam.toLowerCase()) || (list.length === 1 ? list[0] : null)
                if (!match) {
                    setTimelineLoading(false)
                    setTimelineUser({
                        cognitoId: '',
                        email: userParam,
                        name: null,
                        organisation: null,
                        lastIp: null,
                        lastSeen: null,
                    })
                    setResolvedId({ email: userParam, cognitoId: '' })
                    return
                }
                setResolvedId({ email: userParam, cognitoId: match.cognitoId })
            })
            .catch((err) => {
                if (!active) return
                setTimelineLoading(false)
                setTimelineError(errorMessage(err, 'Could not find that user'))
            })
        return () => {
            active = false
        }
    }, [userParam, resolvedId])

    const loadTimeline = useCallback(
        (cognitoId: string, offset: number) => {
            setTimelineLoading(true)
            setTimelineError(null)
            return api.post('activity-timeline', { ...bounds, cognitoId, limit: PAGE_SIZE, offset })
                .then(({ data }) => {
                    if (data?.available === false) {
                        setUnavailable(data.message || 'Activity log unavailable')
                        return
                    }
                    if (!data?.success) {
                        setTimelineError(data?.message || 'Could not load activity')
                        return
                    }
                    setTimelineUser(data.user)
                    setVisitsTotal(data.total || 0)
                    setVisitsTruncated(Boolean(data.truncated))
                    setVisits((current) => (offset === 0 ? data.visits || [] : [...current, ...(data.visits || [])]))
                })
                .catch((err) => setTimelineError(errorMessage(err, 'Could not load activity')))
                .finally(() => setTimelineLoading(false))
        },
        [bounds],
    )

    useEffect(() => {
        if (!userParam || !resolvedId?.cognitoId) return
        if (resolvedId.email.toLowerCase() !== userParam.toLowerCase()) return
        // Keep the current rows on screen while a refresh/range change loads;
        // offset 0 replaces them. Switching user already cleared them above.
        void loadTimeline(resolvedId.cognitoId, 0)
    }, [userParam, resolvedId, loadTimeline])

    // ── Columns ─────────────────────────────────────────────────────────────
    const orgColumns = useMemo<Column<OrgActivity>[]>(
        () => [
            { key: 'organisation', header: 'Organisation', sortable: true, render: (r) => <strong>{r.organisation}</strong> },
            { key: 'users', header: 'Active users', align: 'right', sortable: true },
            { key: 'visits', header: 'Page visits', align: 'right', sortable: true },
            { key: 'actions', header: 'Actions', align: 'right', sortable: true },
            {
                key: 'errors',
                header: 'Errors',
                align: 'right',
                sortable: true,
                render: (r) => (r.errors ? <StatusPill size="sm" tone="bad">{r.errors}</StatusPill> : <span className={styles.muted}>0</span>),
            },
            {
                key: 'lastSeen',
                header: 'Last activity',
                sortable: true,
                sortValue: (r) => (r.lastSeen ? new Date(r.lastSeen).getTime() : 0),
                render: (r) => formatRelative(r.lastSeen),
            },
        ],
        [],
    )

    const userColumns = useMemo<Column<UserActivitySummary>[]>(
        () => [
            {
                key: 'email',
                header: 'User',
                sortable: true,
                render: (r) => (
                    <span className={styles.nameCell}>
                        <strong>{r.name || r.email}</strong>
                        {r.name && <span>{r.email}</span>}
                    </span>
                ),
            },
            ...(searching
                ? [{ key: 'organisation', header: 'Organisation', sortable: true, render: (r: UserActivitySummary) => r.organisation || NO_ORG } as Column<UserActivitySummary>]
                : []),
            { key: 'visits', header: 'Page visits', align: 'right', sortable: true },
            { key: 'actions', header: 'Actions', align: 'right', sortable: true },
            {
                key: 'errors',
                header: 'Errors',
                align: 'right',
                sortable: true,
                render: (r) => (r.errors ? <StatusPill size="sm" tone="bad">{r.errors}</StatusPill> : <span className={styles.muted}>0</span>),
            },
            {
                key: 'lastSeen',
                header: 'Last seen',
                sortable: true,
                sortValue: (r) => (r.lastSeen ? new Date(r.lastSeen).getTime() : 0),
                render: (r) => formatRelative(r.lastSeen),
            },
            { key: 'lastPage', header: 'Last page', truncate: true, render: (r) => (r.lastPage ? pageMeta(r.lastPage).name : '—') },
            { key: 'lastIp', header: 'Last IP', render: (r) => <span className={styles.mono}>{r.lastIp || '—'}</span> },
        ],
        [searching],
    )

    // ── Render ──────────────────────────────────────────────────────────────
    const refreshing = orgsLoading || usersLoading || timelineLoading
    const toolbar = (
        <div className={styles.toolbar}>
            <div className={styles.toolbarGroup}>
                <SegmentedControl
                    aria-label="Time range"
                    options={RANGE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    value={range}
                    onChange={(value) => update({ range: value })}
                />
                <Button
                    size="sm"
                    icon={<FaSyncAlt />}
                    loading={refreshing}
                    onClick={() => setRefreshTick((n) => n + 1)}
                    title="Reload the latest activity"
                >
                    Refresh
                </Button>
            </div>
            <SearchInput
                className={styles.find}
                value={find}
                onChange={setFind}
                placeholder="Find a user by name or email…"
                aria-label="Find a user"
            />
        </div>
    )

    if (unavailable) {
        return (
            <div className={styles.view}>
                <Banner tone="warn" title="Activity log not available yet">
                    {unavailable}
                </Banner>
            </div>
        )
    }

    const usersTable = (title: string, description: string | null, back: () => void, backLabel: string) => (
        <>
            <div className={styles.crumbs}>
                <button type="button" onClick={back}>
                    <FaArrowLeft /> {backLabel}
                </button>
                <span className={styles.sep}>/</span>
                <span className={styles.current}>{title}</span>
            </div>
            <div className={styles.levelHead}>
                <div>
                    <h2 className={styles.levelTitle}>{title}</h2>
                    {description && <div className={styles.levelMeta}>{description}</div>}
                </div>
            </div>
            {!searching && users.length > 0 && (
                <div className={styles.strip}>
                    <StatCard compact label="Active users" value={users.length} />
                    <StatCard compact label="Page visits" value={users.reduce((n, u) => n + u.visits, 0)} />
                    <StatCard compact label="Actions" value={users.reduce((n, u) => n + u.actions, 0)} />
                    {topPages.slice(0, 1).map((p) => (
                        <StatCard
                            key={p.page}
                            compact
                            label="Most visited page"
                            value={pageMeta(p.page).name}
                            format={false}
                            sub={`${p.visits} visit${p.visits === 1 ? '' : 's'}`}
                            wrapLabel
                        />
                    ))}
                </div>
            )}
            <DataTable<UserActivitySummary>
                columns={userColumns}
                rows={users}
                rowKey={(r) => r.cognitoId}
                loading={usersLoading}
                error={usersError}
                emptyTitle={searching ? `No users matching “${debouncedFind}”` : 'No activity in this range'}
                emptyDescription={
                    searching
                        ? 'Only users with recorded activity in the selected range are listed.'
                        : 'Nobody from this organisation has used the dashboard in the selected period.'
                }
                onRowClick={(r) => {
                    setFind('')
                    update({ user: r.email, org: r.organisation || NO_ORG })
                }}
            />
        </>
    )

    // Find box overrides whichever level is showing.
    if (searching) {
        return (
            <div className={styles.view}>
                {toolbar}
                {usersTable(`Users matching “${debouncedFind}”`, null, () => setFind(''), 'Clear search')}
            </div>
        )
    }

    if (userParam) {
        const backToOrg = () => update({ user: null })
        const orgName = timelineUser?.organisation || org || NO_ORG
        return (
            <div className={styles.view}>
                {toolbar}
                <div className={styles.crumbs}>
                    <button type="button" onClick={() => update({ user: null, org: null })}>
                        <FaArrowLeft /> Organisations
                    </button>
                    <span className={styles.sep}>/</span>
                    <button type="button" onClick={backToOrg}>
                        {orgName}
                    </button>
                    <span className={styles.sep}>/</span>
                    <span className={styles.current}>{timelineUser?.name || timelineUser?.email || userParam}</span>
                </div>
                <div className={styles.levelHead}>
                    <div>
                        <h2 className={styles.levelTitle}>{timelineUser?.name || timelineUser?.email || userParam}</h2>
                        <div className={styles.levelMeta}>
                            {timelineUser?.name && <span>{timelineUser.email}</span>}
                            <span>{orgName}</span>
                            {timelineUser?.lastIp && (
                                <span>
                                    Last IP <code>{timelineUser.lastIp}</code>
                                </span>
                            )}
                            {timelineUser?.lastSeen && <span>Last seen {formatRelative(timelineUser.lastSeen)}</span>}
                            {visitsTotal > 0 && (
                                <span>
                                    {visitsTotal} visit{visitsTotal === 1 ? '' : 's'} in range
                                </span>
                            )}
                        </div>
                    </div>
                    <Button as="link" to={`/users?search=${encodeURIComponent(userParam)}`} size="sm">
                        Open in Users
                    </Button>
                </div>
                {resolvedId && resolvedId.cognitoId === '' && resolvedId.email.toLowerCase() === userParam.toLowerCase() ? (
                    <EmptyState
                        title="No recorded activity"
                        description={`Nothing has been recorded for ${userParam} in the last 90 days.`}
                    />
                ) : (
                    <Timeline
                        cognitoId={resolvedId?.cognitoId || ''}
                        visits={visits}
                        loading={timelineLoading}
                        error={timelineError}
                        total={visitsTotal}
                        truncated={visitsTruncated}
                        onLoadMore={() => resolvedId?.cognitoId && loadTimeline(resolvedId.cognitoId, visits.length)}
                    />
                )}
            </div>
        )
    }

    if (org) {
        return (
            <div className={styles.view}>
                {toolbar}
                {usersTable(org, null, () => update({ org: null }), 'Organisations')}
            </div>
        )
    }

    return (
        <div className={styles.view}>
            {toolbar}
            <DataTable<OrgActivity>
                columns={orgColumns}
                rows={orgs}
                rowKey={(r) => r.organisation}
                loading={orgsLoading}
                error={orgsError}
                emptyTitle="No activity in this range"
                emptyDescription="Activity is recorded from the moment this feature is deployed; widen the range or check back later."
                onRowClick={(r) => update({ org: r.organisation })}
            />
        </div>
    )
}
